"use client"

import { useQuery } from "@tanstack/react-query"
import { useRouter } from "next/navigation"
import { useEffect } from "react"
import { toast } from "sonner"
import { getAccountOrderDetail } from "@/api/account-order-detail"
import { Button } from "@/components/ui/button"
import { OrderCustomerDetails } from "./order-customer-details"
import { OrderHeader } from "./order-header"
import { OrderItemsTable } from "./order-items-table"
import { OrderUpdates } from "./order-updates"
import { AccountOrderDetailSkeleton } from "./skeleton"

interface AccountOrderDetailPageProps {
  orderNumber: string
}

export function AccountOrderDetailPage({
  orderNumber,
}: AccountOrderDetailPageProps) {
  const router = useRouter()
  const orderQuery = useQuery({
    queryKey: ["account", "orders", orderNumber],
    queryFn: () => getAccountOrderDetail(orderNumber),
    retry: false,
  })

  useEffect(() => {
    if (!orderQuery.data) {
      return
    }

    if (orderQuery.data.ok) {
      return
    }

    const errorMessage =
      orderQuery.data.message?.trim() ||
      "Não foi possível carregar os detalhes do pedido."

    toast.error(errorMessage)
  }, [orderQuery.data])

  useEffect(() => {
    if (!orderQuery.isError) {
      return
    }

    toast.error("Não foi possível carregar os detalhes do pedido.")
  }, [orderQuery.isError])

  if (orderQuery.isPending) {
    return <AccountOrderDetailSkeleton />
  }

  const hasFailedState = orderQuery.isError || !orderQuery.data?.ok

  if (hasFailedState) {
    return (
      <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
        <div className='max-w-3xl space-y-4'>
          <h1 className='font-semibold text-3xl text-foreground'>
            Erro ao carregar pedido
          </h1>
          <p className='text-base text-muted-foreground leading-7'>
            Não foi possível carregar os detalhes do pedido. Tente novamente em
            instantes.
          </p>
          <div className='flex gap-2'>
            <Button onClick={() => orderQuery.refetch()} type='button'>
              Tentar novamente
            </Button>
            <Button
              onClick={() => router.push("/minha-conta/pedidos")}
              type='button'
              variant='outline'
            >
              Voltar para pedidos
            </Button>
          </div>
        </div>
      </section>
    )
  }

  const order = orderQuery.data.order

  if (!order) {
    return (
      <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
        <div className='max-w-3xl space-y-4'>
          <h1 className='font-semibold text-3xl text-foreground'>
            Pedido não encontrado
          </h1>
          <p className='text-base text-muted-foreground leading-7'>
            O pedido solicitado não foi encontrado ou você não tem permissão
            para visualizá-lo.
          </p>
          <Button
            onClick={() => router.push("/minha-conta/pedidos")}
            type='button'
          >
            Voltar para pedidos
          </Button>
        </div>
      </section>
    )
  }

  return (
    <section className='rounded-2xl border bg-white p-6 shadow-xs md:p-8'>
      <div className='space-y-8'>
        <OrderHeader
          dateCreated={order.dateCreated}
          orderNumber={order.number}
          status={order.status}
        />

        <OrderUpdates notes={order.orderNotes} />

        <OrderItemsTable
          currency={order.currency}
          customerNote={order.customerNote}
          items={order.items}
          totals={order.totals}
        />

        <OrderCustomerDetails
          billingAddress={order.billingAddress}
          needsShipping={order.needsShipping}
          shippingAddress={order.shippingAddress}
        />
      </div>
    </section>
  )
}
